import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

interface BlogPost {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  image: string;
  date: string;
  category: string;
  readTime: string;
  author: string;
  tags: string[];
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
}

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: '2024 Web Tasarım Trendleri',
      excerpt: 'Bu yıl öne çıkan web tasarım trendlerini ve nasıl uygulanacağını keşfedin.',
      content: `
        <h2>2024 Yılının En Önemli Web Tasarım Trendleri</h2>
        
        <p>Web tasarım dünyası sürekli gelişiyor ve 2024 yılı da birçok yenilik getiriyor. Bu yazıda, bu yıl öne çıkan web tasarım trendlerini ve bunları nasıl projelerinizde uygulayabileceğinizi detaylı olarak inceliyoruz.</p>
        
        <h3>1. Minimalist ve Temiz Tasarımlar</h3>
        <p>2024'te minimalizm hala güçlü bir trend olmaya devam ediyor. Temiz çizgiler, bol beyaz alan kullanımı ve sade renk paletleri kullanıcı deneyimini iyileştiriyor. Bu yaklaşım, içeriğin ön plana çıkmasını sağlıyor ve kullanıcıların dikkatini dağıtmıyor.</p>
        
        <h3>2. Koyu Mod (Dark Mode) Tasarımları</h3>
        <p>Koyu mod artık sadece bir seçenek değil, neredeyse bir zorunluluk haline geldi. Kullanıcıların gözlerini yormaması ve batarya tasarrufu sağlaması nedeniyle tercih ediliyor. Modern web sitelerinde hem açık hem koyu mod seçenekleri sunmak önemli.</p>
        
        <h3>3. Mikro Animasyonlar ve Etkileşimler</h3>
        <p>Küçük ama etkili animasyonlar, kullanıcı deneyimini büyük ölçüde iyileştiriyor. Hover efektleri, yükleme animasyonları ve geçiş efektleri sitenizi daha canlı ve profesyonel gösteriyor.</p>
        
        <h3>4. Mobil Öncelikli Tasarım</h3>
        <p>Mobil cihazlardan internet kullanımı masaüstünü geçtiği için, mobil öncelikli tasarım artık bir seçenek değil zorunluluk. Responsive tasarım ve mobil kullanıcı deneyimi öncelik taşıyor.</p>
        
        <h3>5. Sürdürülebilir Web Tasarımı</h3>
        <p>Çevre bilinci artan bir trend olarak web tasarımında da kendini gösteriyor. Daha az enerji tüketen, optimize edilmiş görseller ve temiz kod kullanımı önem kazanıyor.</p>
        
        <h2>Bu Trendleri Nasıl Uygulayabilirsiniz?</h2>
        
        <p>Bu trendleri kendi projelerinizde uygulamak için şu adımları takip edebilirsiniz:</p>
        
        <ul>
          <li><strong>Araştırma yapın:</strong> Hedef kitlenizin tercihlerini ve sektörünüzdeki trendleri inceleyin</li>
          <li><strong>Aşamalı geçiş:</strong> Tüm tasarımı bir anda değiştirmek yerine aşamalı güncellemeler yapın</li>
          <li><strong>Test edin:</strong> Yeni tasarım öğelerini kullanıcılarla test edin ve geri bildirim alın</li>
          <li><strong>Performansı izleyin:</strong> Tasarım değişikliklerinin site performansına etkisini takip edin</li>
        </ul>
        
        <h2>Sonuç</h2>
        
        <p>2024 web tasarım trendleri, kullanıcı deneyimini ön planda tutan, sürdürülebilir ve erişilebilir çözümler sunuyor. Bu trendleri takip ederek, modern ve etkili web siteleri oluşturabilirsiniz.</p>
        
        <p>Web tasarım projelerinizde profesyonel destek almak istiyorsanız, <a href="/iletisim" class="text-blue-600 hover:text-purple-600">bizimle iletişime geçebilirsiniz</a>.</p>
      `,
      image: 'https://public.readdy.ai/ai/img_res/0295101bc9b4f0860d33425d43fa9ca9.jpg',
      date: '19 Aralık 2024',
      category: 'Web Tasarım',
      readTime: '5 dk',
      author: 'Webcoll Ekibi',
      tags: ['Web Tasarım', 'Trendler', 'UX/UI', 'Minimalizm', 'Responsive'],
      seoTitle: '2024 Web Tasarım Trendleri | Modern Web Sitesi Tasarım İpuçları',
      seoDescription: '2024 yılının en güncel web tasarım trendlerini keşfedin. Minimalist tasarım, koyu mod, mikro animasyonlar ve mobil öncelikli yaklaşımlar hakkında detaylı rehber.',
      seoKeywords: 'web tasarım trendleri 2024, modern web tasarım, minimalist tasarım, responsive tasarım, UX UI tasarım'
    },
    {
      id: 2,
      title: 'SEO İpuçları: Google\'da Üst Sıralara Çıkın',
      excerpt: 'Web sitenizin arama motorlarında daha iyi sıralanması için pratik SEO teknikleri.',
      content: `
        <h2>SEO ile Google'da Üst Sıralara Çıkmanın Sırları</h2>
        
        <p>Arama motoru optimizasyonu (SEO), web sitenizin Google'da daha iyi sıralanması için kritik öneme sahip. Bu kapsamlı rehberde, SEO'nun temellerinden ileri seviye tekniklere kadar her şeyi öğreneceksiniz.</p>
        
        <h3>1. Anahtar Kelime Araştırması</h3>
        <p>SEO'nun temeli doğru anahtar kelimeleri bulmaktan geçer. Hedef kitlenizin ne aradığını anlamak ve bu terimleri içeriğinizde stratejik olarak kullanmak önemlidir.</p>
        
        <ul>
          <li>Google Keyword Planner kullanın</li>
          <li>Uzun kuyruk anahtar kelimelere odaklanın</li>
          <li>Rakip analizi yapın</li>
          <li>Yerel arama terimlerini unutmayın</li>
        </ul>
        
        <h3>2. İçerik Optimizasyonu</h3>
        <p>Kaliteli içerik SEO'nun kalbidir. Google, kullanıcılara değer katan, özgün ve kapsamlı içerikleri ödüllendiriyor.</p>
        
        <h4>İçerik Yazarken Dikkat Edilecekler:</h4>
        <ul>
          <li>Başlık etiketlerini (H1, H2, H3) doğru kullanın</li>
          <li>Meta açıklamalarını optimize edin</li>
          <li>İç bağlantılar oluşturun</li>
          <li>Görsellere alt text ekleyin</li>
        </ul>
        
        <h3>3. Teknik SEO</h3>
        <p>Web sitenizin teknik altyapısı SEO performansınızı doğrudan etkiler. Site hızı, mobil uyumluluk ve güvenlik faktörleri kritik öneme sahip.</p>
        
        <h4>Teknik SEO Kontrol Listesi:</h4>
        <ul>
          <li>Site hızını optimize edin (3 saniye altı)</li>
          <li>Mobil uyumluluğu sağlayın</li>
          <li>SSL sertifikası kullanın</li>
          <li>XML sitemap oluşturun</li>
          <li>Robots.txt dosyasını yapılandırın</li>
        </ul>
        
        <h3>4. Yerel SEO</h3>
        <p>Yerel işletmeler için yerel SEO hayati önem taşır. Google My Business optimizasyonu ve yerel anahtar kelimeler kullanımı kritiktir.</p>
        
        <h3>5. Bağlantı Kurma (Link Building)</h3>
        <p>Kaliteli backlink'ler sitenizin otoritesini artırır. Doğal ve organik bağlantı kurma stratejileri geliştirin.</p>
        
        <h2>SEO Sonuçlarını Nasıl Ölçersiniz?</h2>
        
        <p>SEO çalışmalarınızın etkisini ölçmek için şu araçları kullanabilirsiniz:</p>
        
        <ul>
          <li>Google Analytics</li>
          <li>Google Search Console</li>
          <li>SEMrush veya Ahrefs</li>
          <li>PageSpeed Insights</li>
        </ul>
        
        <h2>Sonuç</h2>
        
        <p>SEO uzun vadeli bir yatırımdır ve sabır gerektirir. Doğru stratejilerle ve sürekli optimizasyonla Google'da üst sıralara çıkmak mümkün.</p>
        
        <p>SEO konusunda profesyonel destek almak istiyorsanız, <a href="/iletisim" class="text-blue-600 hover:text-purple-600">uzman ekibimizle iletişime geçin</a>.</p>
      `,
      image: 'https://readdy.ai/api/search-image?query=SEO%20optimization%20concept%2C%20search%20engine%20ranking%2C%20Google%20analytics%20dashboard%2C%20keyword%20research%20tools%2C%20digital%20marketing%20strategy%2C%20website%20traffic%20growth%20visualization&width=800&height=400&seq=blog2detail&orientation=landscape',
      date: '18 Aralık 2024',
      category: 'SEO',
      readTime: '7 dk',
      author: 'Webcoll Ekibi',
      tags: ['SEO', 'Google', 'Arama Motoru', 'Dijital Pazarlama', 'İçerik'],
      seoTitle: 'SEO İpuçları 2024 | Google\'da Üst Sıralara Çıkma Rehberi',
      seoDescription: 'Web sitenizin Google\'da üst sıralara çıkması için pratik SEO teknikleri. Anahtar kelime araştırması, içerik optimizasyonu ve teknik SEO rehberi.',
      seoKeywords: 'SEO ipuçları, Google sıralama, arama motoru optimizasyonu, SEO teknikleri, web sitesi SEO'
    },
    {
      id: 3,
      title: 'E-ticaret Sitesi Açmanın Püf Noktaları',
      excerpt: 'Başarılı bir online mağaza kurmak için bilmeniz gereken her şey.',
      content: `
        <h2>E-ticaret Sitesi Açma Rehberi: Başarılı Online Mağaza Kurmanın Sırları</h2>
        
        <p>E-ticaret dünyası hızla büyüyor ve doğru stratejilerle bu büyümeden pay almak mümkün. Bu kapsamlı rehberde, başarılı bir e-ticaret sitesi kurmanın tüm aşamalarını detaylı olarak inceliyoruz.</p>
        
        <h3>1. Pazar Araştırması ve Niş Belirleme</h3>
        <p>Başarılı bir e-ticaret sitesi kurmanın ilk adımı doğru pazarı ve nişi belirlemektir. Rekabet analizi yapın ve hedef kitlenizi tanımlayın.</p>
        
        <h4>Pazar Araştırması İçin:</h4>
        <ul>
          <li>Rakip analizi yapın</li>
          <li>Hedef kitle demografilerini belirleyin</li>
          <li>Talep ve arz dengesini inceleyin</li>
          <li>Fiyat stratejilerini araştırın</li>
        </ul>
        
        <h3>2. Platform Seçimi</h3>
        <p>E-ticaret platformu seçimi kritik bir karardır. İhtiyaçlarınıza uygun platformu seçmek uzun vadeli başarınızı etkiler.</p>
        
        <h4>Popüler E-ticaret Platformları:</h4>
        <ul>
          <li><strong>Shopify:</strong> Kullanıcı dostu, hızlı kurulum</li>
          <li><strong>WooCommerce:</strong> WordPress tabanlı, esnek</li>
          <li><strong>Magento:</strong> Büyük işletmeler için güçlü</li>
          <li><strong>OpenCart:</strong> Açık kaynak, özelleştirilebilir</li>
        </ul>
        
        <h3>3. Web Sitesi Tasarımı ve Kullanıcı Deneyimi</h3>
        <p>E-ticaret sitesinin tasarımı satışlarınızı doğrudan etkiler. Kullanıcı dostu, hızlı ve güvenilir bir site tasarlamak önemlidir.</p>
        
        <h4>Tasarım İlkeleri:</h4>
        <ul>
          <li>Temiz ve profesyonel görünüm</li>
          <li>Kolay navigasyon</li>
          <li>Hızlı yükleme süreleri</li>
          <li>Mobil uyumluluk</li>
          <li>Güvenlik sertifikaları</li>
        </ul>
        
        <h3>4. Ürün Yönetimi ve Katalog Oluşturma</h3>
        <p>Ürünlerinizi etkili şekilde sunmak satış oranlarınızı artırır. Kaliteli ürün fotoğrafları ve detaylı açıklamalar kritiktir.</p>
        
        <h3>5. Ödeme ve Kargo Sistemleri</h3>
        <p>Güvenli ödeme seçenekleri ve hızlı kargo hizmetleri müşteri memnuniyetini artırır.</p>
        
        <h4>Ödeme Seçenekleri:</h4>
        <ul>
          <li>Kredi kartı (Visa, MasterCard)</li>
          <li>Banka havalesi</li>
          <li>Kapıda ödeme</li>
          <li>Dijital cüzdanlar (PayPal, Apple Pay)</li>
        </ul>
        
        <h3>6. SEO ve Dijital Pazarlama</h3>
        <p>E-ticaret sitesinin görünürlüğünü artırmak için SEO ve dijital pazarlama stratejileri geliştirin.</p>
        
        <h3>7. Müşteri Hizmetleri ve Destek</h3>
        <p>Kaliteli müşteri hizmetleri müşteri sadakatini artırır ve tekrar satış oranlarını yükseltir.</p>
        
        <h2>Yasal Gereklilikler</h2>
        
        <p>E-ticaret sitesi açarken dikkat etmeniz gereken yasal konular:</p>
        
        <ul>
          <li>Ticaret sicil kaydı</li>
          <li>Vergi levhası</li>
          <li>KVKK uyumluluk</li>
          <li>Mesafeli satış sözleşmesi</li>
          <li>İade ve değişim politikaları</li>
        </ul>
        
        <h2>Sonuç</h2>
        
        <p>Başarılı bir e-ticaret sitesi kurmak detaylı planlama ve doğru stratejiler gerektirir. Sabırlı olun ve sürekli optimizasyon yapın.</p>
        
        <p>E-ticaret sitenizi kurmak için profesyonel destek almak istiyorsanız, <a href="/iletisim" class="text-blue-600 hover:text-purple-600">uzman ekibimizle görüşün</a>.</p>
      `,
      image: 'https://readdy.ai/api/search-image?query=E-commerce%20website%20design%2C%20online%20shopping%20interface%2C%20product%20catalog%20layout%2C%20shopping%20cart%20design%2C%20payment%20gateway%20integration%2C%20mobile%20commerce%20experience&width=800&height=400&seq=blog3detail&orientation=landscape',
      date: '17 Aralık 2024',
      category: 'E-ticaret',
      readTime: '6 dk',
      author: 'Webcoll Ekibi',
      tags: ['E-ticaret', 'Online Mağaza', 'Dijital Satış', 'Web Tasarım', 'Pazarlama'],
      seoTitle: 'E-ticaret Sitesi Açma Rehberi 2024 | Online Mağaza Kurma',
      seoDescription: 'Başarılı e-ticaret sitesi açmanın püf noktaları. Platform seçimi, tasarım, ödeme sistemleri ve yasal gereklilikler hakkında kapsamlı rehber.',
      seoKeywords: 'e-ticaret sitesi açma, online mağaza kurma, e-ticaret rehberi, dijital satış, web mağaza'
    },
    {
      id: 4,
      title: 'Mobil Uyumlu Tasarımın Önemi',
      excerpt: 'Neden her web sitesi mobil cihazlar için optimize edilmeli?',
      content: `
        <h2>Mobil Uyumlu Tasarım: Dijital Çağın Zorunluluğu</h2>
        
        <p>Günümüzde internet kullanıcılarının %60'ından fazlası mobil cihazlardan web sitelerine erişiyor. Bu durum, mobil uyumlu tasarımı bir seçenek olmaktan çıkarıp zorunluluk haline getiriyor.</p>
        
        <h3>1. Mobil Kullanım İstatistikleri</h3>
        <p>Türkiye'de ve dünyada mobil internet kullanımı sürekli artıyor. İşte dikkat çekici istatistikler:</p>
        
        <ul>
          <li>Mobil cihazlardan internet kullanımı %65'i geçti</li>
          <li>Kullanıcıların %57'si mobil uyumlu olmayan siteleri terk ediyor</li>
          <li>Google arama sonuçlarının %60'ı mobil cihazlardan geliyor</li>
          <li>E-ticaret satışlarının %45'i mobil cihazlardan gerçekleşiyor</li>
        </ul>
        
        <h3>2. Google'ın Mobile-First İndeksleme Yaklaşımı</h3>
        <p>Google, 2019'dan itibaren mobile-first indeksleme yaklaşımını benimsiyor. Bu, web sitenizin mobil versiyonunun arama sıralamanızı belirlediği anlamına geliyor.</p>
        
        <h4>Mobile-First İndekslemenin Etkileri:</h4>
        <ul>
          <li>Mobil site performansı SEO'yu doğrudan etkiliyor</li>
          <li>Sayfa yükleme hızı kritik önem taşıyor</li>
          <li>Mobil kullanıcı deneyimi sıralama faktörü</li>
          <li>Responsive tasarım zorunlu hale geldi</li>
        </ul>
        
        <h3>3. Responsive Tasarım vs Mobil Site</h3>
        <p>Mobil uyumluluk için iki ana yaklaşım var: responsive tasarım ve ayrı mobil site. Her ikisinin de avantaj ve dezavantajları bulunuyor.</p>
        
        <h4>Responsive Tasarım Avantajları:</h4>
        <ul>
          <li>Tek URL yapısı</li>
          <li>Kolay yönetim</li>
          <li>SEO dostu</li>
          <li>Maliyet etkin</li>
        </ul>
        
        <h4>Ayrı Mobil Site Avantajları:</h4>
        <ul>
          <li>Mobil özel özellikler</li>
          <li>Daha hızlı yükleme</li>
          <li>Özelleştirilmiş deneyim</li>
          <li>Daha iyi performans</li>
        </ul>
        
        <h3>4. Mobil Kullanıcı Deneyimi (UX) İlkeleri</h3>
        <p>Mobil cihazlarda kullanıcı deneyimi masaüstünden farklı yaklaşımlar gerektirir.</p>
        
        <h4>Mobil UX En İyi Uygulamaları:</h4>
        <ul>
          <li>Dokunmatik dostu butonlar (minimum 44px)</li>
          <li>Basit ve temiz navigasyon</li>
          <li>Hızlı yükleme süreleri (3 saniye altı)</li>
          <li>Okunabilir font boyutları (minimum 16px)</li>
          <li>Kolay form doldurma</li>
        </ul>
        
        <h3>5. Mobil Site Hızı Optimizasyonu</h3>
        <p>Mobil cihazlarda site hızı daha da kritik. Yavaş yüklenen siteler yüksek terk oranlarına neden oluyor.</p>
        
        <h4>Hız Optimizasyon Teknikleri:</h4>
        <ul>
          <li>Görsel optimizasyonu (WebP formatı)</li>
          <li>CSS ve JavaScript minifikasyonu</li>
          <li>CDN kullanımı</li>
          <li>Lazy loading uygulaması</li>
          <li>Cache stratejileri</li>
        </ul>
        
        <h3>6. Mobil SEO İpuçları</h3>
        <p>Mobil SEO, geleneksel SEO'dan farklı yaklaşımlar gerektirir.</p>
        
        <ul>
          <li>Yerel arama optimizasyonu</li>
          <li>Sesli arama uyumluluğu</li>
          <li>Structured data kullanımı</li>
          <li>AMP (Accelerated Mobile Pages) implementasyonu</li>
        </ul>
        
        <h3>7. Mobil Analitik ve Ölçüm</h3>
        <p>Mobil performansınızı ölçmek için doğru metrikleri takip etmek önemli.</p>
        
        <h4>Önemli Mobil Metrikler:</h4>
        <ul>
          <li>Mobil trafik oranı</li>
          <li>Mobil dönüşüm oranları</li>
          <li>Sayfa yükleme süreleri</li>
          <li>Bounce rate (terk oranı)</li>
          <li>Mobil kullanıcı davranışları</li>
        </ul>
        
        <h2>Mobil Uyumluluk Testi</h2>
        
        <p>Sitenizin mobil uyumluluğunu test etmek için şu araçları kullanabilirsiniz:</p>
        
        <ul>
          <li>Google Mobile-Friendly Test</li>
          <li>PageSpeed Insights</li>
          <li>GTmetrix</li>
          <li>WebPageTest</li>
        </ul>
        
        <h2>Sonuç</h2>
        
        <p>Mobil uyumlu tasarım artık bir lüks değil, zorunluluk. Kullanıcılarınızın büyük çoğunluğu mobil cihazlardan sitenize erişiyor ve bu deneyimi optimize etmek başarınız için kritik.</p>
        
        <p>Mobil uyumlu web siteniz için profesyonel destek almak istiyorsanız, <a href="/iletisim" class="text-blue-600 hover:text-purple-600">uzman ekibimizle iletişime geçin</a>.</p>
      `,
      image: 'https://readdy.ai/api/search-image?query=Mobile%20responsive%20web%20design%2C%20smartphone%20tablet%20interface%2C%20adaptive%20layout%20design%2C%20mobile%20user%20experience%2C%20cross-device%20compatibility%2C%20responsive%20grid%20system&width=800&height=400&seq=blog4detail&orientation=landscape',
      date: '16 Aralık 2024',
      category: 'Mobil Tasarım',
      readTime: '4 dk',
      author: 'Webcoll Ekibi',
      tags: ['Mobil Tasarım', 'Responsive', 'UX', 'SEO', 'Performans'],
      seoTitle: 'Mobil Uyumlu Tasarım 2024 | Responsive Web Tasarım Rehberi',
      seoDescription: 'Mobil uyumlu web tasarımın önemi ve responsive tasarım teknikleri. Mobile-first yaklaşım, kullanıcı deneyimi ve SEO optimizasyonu rehberi.',
      seoKeywords: 'mobil uyumlu tasarım, responsive web tasarım, mobil SEO, mobile-first, kullanıcı deneyimi'
    },
    {
      id: 5,
      title: 'Sosyal Medya Entegrasyonu Rehberi',
      excerpt: 'Web sitenizi sosyal medya platformlarıyla nasıl entegre edersiniz?',
      content: `
        <h2>Sosyal Medya Entegrasyonu: Web Sitenizi Sosyal Ağlarla Güçlendirin</h2>
        
        <p>Sosyal medya entegrasyonu, web sitenizin erişimini artıran ve kullanıcı etkileşimini güçlendiren kritik bir stratejidir. Bu rehberde, sosyal medya entegrasyonunun tüm yönlerini detaylı olarak inceliyoruz.</p>
        
        <h3>1. Sosyal Medya Entegrasyonunun Önemi</h3>
        <p>Günümüzde sosyal medya platformları milyarlarca kullanıcıya sahip. Web sitenizi bu platformlarla entegre etmek birçok avantaj sağlar:</p>
        
        <ul>
          <li>Organik trafik artışı</li>
          <li>Marka bilinirliğinin artması</li>
          <li>Kullanıcı etkileşiminin güçlenmesi</li>
          <li>SEO performansının iyileşmesi</li>
          <li>Sosyal kanıt oluşturma</li>
        </ul>
        
        <h3>2. Temel Sosyal Medya Entegrasyon Yöntemleri</h3>
        
        <h4>A) Sosyal Medya Paylaşım Butonları</h4>
        <p>İçeriklerinizin kolayca paylaşılmasını sağlayan butonlar ekleyin:</p>
        
        <ul>
          <li>Facebook paylaş butonu</li>
          <li>Twitter tweet butonu</li>
          <li>LinkedIn paylaş butonu</li>
          <li>Instagram hikaye paylaşımı</li>
          <li>WhatsApp paylaş butonu</li>
        </ul>
        
        <h4>B) Sosyal Medya Beslemeleri</h4>
        <p>Sosyal medya hesaplarınızdaki içerikleri web sitenizde gösterin:</p>
        
        <ul>
          <li>Instagram feed entegrasyonu</li>
          <li>Twitter timeline widget'ı</li>
          <li>Facebook sayfa plugin'i</li>
          <li>YouTube video galerisi</li>
        </ul>
        
        <h4>C) Sosyal Giriş (Social Login)</h4>
        <p>Kullanıcıların sosyal medya hesaplarıyla giriş yapmasını sağlayın:</p>
        
        <ul>
          <li>Facebook ile giriş</li>
          <li>Google ile giriş</li>
          <li>Twitter ile giriş</li>
          <li>LinkedIn ile giriş</li>
        </ul>
        
        <h3>3. Platform Bazında Entegrasyon Stratejileri</h3>
        
        <h4>Facebook Entegrasyonu</h4>
        <p>Facebook, en geniş kullanıcı kitlesine sahip platform olarak özel önem taşır:</p>
        
        <ul>
          <li>Facebook Pixel kurulumu</li>
          <li>Open Graph meta etiketleri</li>
          <li>Facebook Comments plugin'i</li>
          <li>Facebook Messenger entegrasyonu</li>
          <li>Facebook Shop entegrasyonu</li>
        </ul>
        
        <h4>Instagram Entegrasyonu</h4>
        <p>Görsel içerik odaklı markalar için kritik:</p>
        
        <ul>
          <li>Instagram Basic Display API</li>
          <li>Instagram feed widget'ları</li>
          <li>Instagram Stories entegrasyonu</li>
          <li>Shoppable posts</li>
        </ul>
        
        <h4>Twitter Entegrasyonu</h4>
        <p>Haber ve güncel içerik paylaşımı için ideal:</p>
        
        <ul>
          <li>Twitter Cards optimizasyonu</li>
          <li>Tweet embed'leri</li>
          <li>Twitter timeline widget'ı</li>
          <li>Hashtag takibi</li>
        </ul>
        
        <h4>LinkedIn Entegrasyonu</h4>
        <p>B2B işletmeler için vazgeçilmez:</p>
        
        <ul>
          <li>LinkedIn Company Page plugin'i</li>
          <li>LinkedIn Share button</li>
          <li>LinkedIn Insight Tag</li>
          <li>Professional profil entegrasyonu</li>
        </ul>
        
        <h3>4. Teknik Uygulama Detayları</h3>
        
        <h4>Open Graph Meta Etiketleri</h4>
        <p>Sosyal medyada paylaşıldığında içeriğinizin nasıl görüneceğini kontrol edin:</p>
        
        <ul>
          <li>og:title - Paylaşım başlığı</li>
          <li>og:description - Paylaşım açıklaması</li>
          <li>og:image - Paylaşım görseli</li>
          <li>og:url - Sayfa URL'i</li>
          <li>og:type - İçerik tipi</li>
        </ul>
        
        <h4>Twitter Cards</h4>
        <p>Twitter'da zengin içerik görünümü sağlayın:</p>
        
        <ul>
          <li>Summary Card</li>
          <li>Summary Card with Large Image</li>
          <li>App Card</li>
          <li>Player Card</li>
        </ul>
        
        <h3>5. Sosyal Medya SEO Faydaları</h3>
        <p>Sosyal medya entegrasyonu SEO performansınızı da olumlu etkiler:</p>
        
        <ul>
          <li>Sosyal sinyaller (social signals)</li>
          <li>Backlink potansiyeli</li>
          <li>Marka aramaları artışı</li>
          <li>İçerik keşfedilebilirliği</li>
          <li>Kullanıcı etkileşimi artışı</li>
        </ul>
        
        <h3>6. Performans ve Analitik</h3>
        <p>Sosyal medya entegrasyonunuzun etkisini ölçün:</p>
        
        <h4>Takip Edilmesi Gereken Metrikler:</h4>
        <ul>
          <li>Sosyal medyadan gelen trafik</li>
          <li>Paylaşım sayıları</li>
          <li>Sosyal dönüşüm oranları</li>
          <li>Engagement oranları</li>
          <li>Sosyal ROI</li>
        </ul>
        
        <h3>7. En İyi Uygulamalar ve İpuçları</h3>
        
        <ul>
          <li><strong>Hız optimizasyonu:</strong> Sosyal widget'lar site hızını etkileyebilir</li>
          <li><strong>Mobil uyumluluk:</strong> Tüm sosyal özellikler mobilde çalışmalı</li>
          <li><strong>Gizlilik:</strong> GDPR ve KVKK uyumluluğunu sağlayın</li>
          <li><strong>Güvenlik:</strong> API anahtarlarını güvenli saklayın</li>
          <li><strong>Güncellik:</strong> Platform değişikliklerini takip edin</li>
        </ul>
        
        <h2>Yaygın Hatalar ve Çözümleri</h2>
        
        <ul>
          <li><strong>Çok fazla widget:</strong> Site hızını yavaşlatabilir</li>
          <li><strong>Güncel olmayan API'lar:</strong> Düzenli güncelleme yapın</li>
          <li><strong>Mobil uyumsuzluk:</strong> Responsive tasarım kullanın</li>
          <li><strong>SEO ihmal:</strong> Meta etiketleri optimize edin</li>
        </ul>
        
        <h2>Sonuç</h2>
        
        <p>Sosyal medya entegrasyonu, modern web sitelerinin vazgeçilmez bir parçasıdır. Doğru stratejilerle uygulandığında, web sitenizin erişimini ve etkileşimini önemli ölçüde artırabilir.</p>
        
        <p>Sosyal medya entegrasyonu konusunda profesyonel destek almak istiyorsanız, <a href="/iletisim" class="text-blue-600 hover:text-purple-600">uzman ekibimizle görüşün</a>.</p>
      `,
      image: 'https://readdy.ai/api/search-image?query=Social%20media%20integration%2C%20website%20social%20sharing%20buttons%2C%20Instagram%20Facebook%20Twitter%20integration%2C%20social%20media%20marketing%20strategy%2C%20digital%20presence%20optimization&width=800&height=400&seq=blog5detail&orientation=landscape',
      date: '15 Aralık 2024',
      category: 'Sosyal Medya',
      readTime: '5 dk',
      author: 'Webcoll Ekibi',
      tags: ['Sosyal Medya', 'Entegrasyon', 'Dijital Pazarlama', 'SEO', 'Web Tasarım'],
      seoTitle: 'Sosyal Medya Entegrasyonu 2024 | Web Sitesi Sosyal Ağ Bağlantısı',
      seoDescription: 'Web sitenizi sosyal medya platformlarıyla entegre etme rehberi. Facebook, Instagram, Twitter entegrasyonu ve sosyal medya SEO teknikleri.',
      seoKeywords: 'sosyal medya entegrasyonu, web sitesi sosyal medya, Facebook entegrasyon, Instagram feed, sosyal medya SEO'
    },
    {
      id: 6,
      title: 'Web Sitesi Hızlandırma Teknikleri',
      excerpt: 'Sitenizin yükleme hızını artırmak için uygulayabileceğiniz yöntemler.',
      content: `
        <h2>Web Sitesi Hızlandırma: Performans Optimizasyonunun Kapsamlı Rehberi</h2>
        
        <p>Web sitesi hızı, kullanıcı deneyimi ve SEO açısından kritik öneme sahip. Yavaş yüklenen siteler yüksek terk oranlarına ve düşük dönüşüm oranlarına neden oluyor. Bu rehberde, web sitenizin hızını artırmak için uygulayabileceğiniz tüm teknikleri detaylı olarak inceliyoruz.</p>
        
        <h3>1. Web Sitesi Hızının Önemi</h3>
        <p>Site hızı neden bu kadar önemli? İşte dikkat çekici istatistikler:</p>
        
        <ul>
          <li>1 saniye gecikme %7 dönüşüm kaybına neden oluyor</li>
          <li>3 saniyeden uzun yüklenen siteleri kullanıcıların %40'ı terk ediyor</li>
          <li>Google, site hızını sıralama faktörü olarak kullanıyor</li>
          <li>Hızlı siteler %2.5 kat daha fazla dönüşüm sağlıyor</li>
        </ul>
        
        <h3>2. Site Hızını Ölçme Araçları</h3>
        <p>Optimizasyona başlamadan önce mevcut durumunuzu ölçün:</p>
        
        <h4>Ücretsiz Test Araçları:</h4>
        <ul>
          <li><strong>Google PageSpeed Insights:</strong> Google'ın resmi aracı</li>
          <li><strong>GTmetrix:</strong> Detaylı analiz ve öneriler</li>
          <li><strong>WebPageTest:</strong> Kapsamlı performans testi</li>
          <li><strong>Pingdom:</strong> Kullanıcı dostu interface</li>
          <li><strong>Google Lighthouse:</strong> Chrome DevTools entegrasyonu</li>
        </ul>
        
        <h3>3. Görsel Optimizasyonu</h3>
        <p>Görseller genellikle sayfa boyutunun %60-70'ini oluşturur. Görsel optimizasyonu en büyük hız artışını sağlar.</p>
        
        <h4>Görsel Optimizasyon Teknikleri:</h4>
        <ul>
          <li><strong>Format seçimi:</strong> WebP > JPEG > PNG</li>
          <li><strong>Sıkıştırma:</strong> Kalite kaybı olmadan boyut azaltma</li>
          <li><strong>Boyut optimizasyonu:</strong> Responsive images kullanımı</li>
          <li><strong>Lazy loading:</strong> Görünür alandaki görselleri önce yükle</li>
          <li><strong>CDN kullanımı:</strong> Görselleri hızlı sunucularda barındır</li>
        </ul>
        
        <h4>Görsel Optimizasyon Araçları:</h4>
        <ul>
          <li>TinyPNG - Online sıkıştırma</li>
          <li>ImageOptim - Mac için</li>
          <li>RIOT - Windows için</li>
          <li>Squoosh - Google'ın web aracı</li>
        </ul>
        
        <h3>4. CSS ve JavaScript Optimizasyonu</h3>
        <p>Kod optimizasyonu site hızını önemli ölçüde etkiler.</p>
        
        <h4>CSS Optimizasyonu:</h4>
        <ul>
          <li><strong>Minifikasyon:</strong> Gereksiz karakterleri kaldır</li>
          <li><strong>Critical CSS:</strong> Önemli stilleri inline olarak ekle</li>
          <li><strong>CSS birleştirme:</strong> Birden fazla dosyayı tek dosyada topla</li>
          <li><strong>Unused CSS:</strong> Kullanılmayan stilleri temizle</li>
        </ul>
        
        <h4>JavaScript Optimizasyonu:</h4>
        <ul>
          <li><strong>Minifikasyon:</strong> Kod boyutunu küçült</li>
          <li><strong>Async/Defer:</strong> Asenkron yükleme kullan</li>
          <li><strong>Code splitting:</strong> Kodu parçalara böl</li>
          <li><strong>Tree shaking:</strong> Kullanılmayan kodu kaldır</li>
        </ul>
        
        <h3>5. Sunucu ve Hosting Optimizasyonu</h3>
        <p>Kaliteli hosting ve sunucu konfigürasyonu temel gereksinimdir.</p>
        
        <h4>Hosting Seçimi:</h4>
        <ul>
          <li><strong>SSD depolama:</strong> HDD'den 10 kat hızlı</li>
          <li><strong>HTTP/2 desteği:</strong> Paralel dosya yükleme</li>
          <li><strong>CDN entegrasyonu:</strong> Global içerik dağıtımı</li>
          <li><strong>Sunucu konumu:</strong> Hedef kitleye yakın lokasyon</li>
        </ul>
        
        <h4>Sunucu Konfigürasyonu:</h4>
        <ul>
          <li>Gzip sıkıştırma aktifleştirme</li>
          <li>Browser caching ayarları</li>
          <li>Keep-alive bağlantıları</li>
          <li>Server response time optimizasyonu</li>
        </ul>
        
        <h3>6. Caching (Önbellekleme) Stratejileri</h3>
        <p>Önbellekleme, tekrar ziyaretlerde dramatik hız artışı sağlar.</p>
        
        <h4>Caching Türleri:</h4>
        <ul>
          <li><strong>Browser caching:</strong> Kullanıcı tarayıcısında saklama</li>
          <li><strong>Server caching:</strong> Sunucu tarafında önbellekleme</li>
          <li><strong>CDN caching:</strong> Global önbellekleme ağı</li>
          <li><strong>Database caching:</strong> Veritabanı sorgu önbellekleme</li>
        </ul>
        
        <h4>WordPress için Caching Plugin'leri:</h4>
        <ul>
          <li>WP Rocket (Premium)</li>
          <li>W3 Total Cache (Ücretsiz)</li>
          <li>WP Super Cache (Ücretsiz)</li>
          <li>LiteSpeed Cache (Ücretsiz)</li>
        </ul>
        
        <h3>7. CDN (Content Delivery Network) Kullanımı</h3>
        <p>CDN, içeriklerinizi dünya çapında dağıtarak hız artışı sağlar.</p>
        
        <h4>Popüler CDN Sağlayıcıları:</h4>
        <ul>
          <li><strong>Cloudflare:</strong> Ücretsiz plan mevcut</li>
          <li><strong>Amazon CloudFront:</strong> AWS entegrasyonu</li>
          <li><strong>MaxCDN:</strong> Kullanıcı dostu</li>
          <li><strong>KeyCDN:</strong> Uygun fiyatlı</li>
        </ul>
        
        <h3>8. Veritabanı Optimizasyonu</h3>
        <p>Veritabanı performansı dinamik sitelerde kritik öneme sahip.</p>
        
        <h4>Veritabanı Optimizasyon Teknikleri:</h4>
        <ul>
          <li>Gereksiz plugin'leri kaldırma</li>
          <li>Veritabanı temizliği</li>
          <li>İndeks optimizasyonu</li>
          <li>Sorgu optimizasyonu</li>
          <li>Spam ve revizyon temizliği</li>
        </ul>
        
        <h3>9. Mobil Performans Optimizasyonu</h3>
        <p>Mobil cihazlarda performans daha da kritik.</p>
        
        <h4>Mobil Optimizasyon İpuçları:</h4>
        <ul>
          <li>AMP (Accelerated Mobile Pages) kullanımı</li>
          <li>Progressive Web App (PWA) teknolojisi</li>
          <li>Touch-friendly tasarım</li>
          <li>Mobil-first yaklaşım</li>
        </ul>
        
        <h3>10. İleri Seviye Optimizasyon Teknikleri</h3>
        
        <h4>HTTP/2 ve HTTP/3:</h4>
        <ul>
          <li>Multiplexing desteği</li>
          <li>Server push özelliği</li>
          <li>Header compression</li>
        </ul>
        
        <h4>Service Workers:</h4>
        <ul>
          <li>Offline çalışma desteği</li>
          <li>Background sync</li>
          <li>Push notifications</li>
        </ul>
        
        <h2>Performans İzleme ve Sürekli Optimizasyon</h2>
        
        <p>Site hızı optimizasyonu tek seferlik bir iş değil, sürekli izleme gerektirir:</p>
        
        <ul>
          <li>Düzenli performans testleri</li>
          <li>Real User Monitoring (RUM)</li>
          <li>Core Web Vitals takibi</li>
          <li>A/B testing ile optimizasyon</li>
        </ul>
        
        <h2>Sonuç</h2>
        
        <p>Web sitesi hızlandırma, teknik bilgi ve sabır gerektiren bir süreçtir. Doğru teknikleri uygulayarak sitenizin performansını önemli ölçüde artırabilir, kullanıcı deneyimini iyileştirebilir ve SEO sıralamanızı yükseltebilirsiniz.</p>
        
        <p>Web sitenizin hızlandırılması konusunda profesyonel destek almak istiyorsanız, <a href="/iletisim" class="text-blue-600 hover:text-purple-600">uzman ekibimizle iletişime geçin</a>.</p>
      `,
      image: 'https://readdy.ai/api/search-image?query=Website%20speed%20optimization%2C%20fast%20loading%20web%20pages%2C%20performance%20metrics%20dashboard%2C%20page%20speed%20insights%2C%20web%20performance%20tools%2C%20loading%20time%20optimization&width=800&height=400&seq=blog6detail&orientation=landscape',
      date: '14 Aralık 2024',
      category: 'Performans',
      readTime: '8 dk',
      author: 'Webcoll Ekibi',
      tags: ['Performans', 'Hızlandırma', 'Optimizasyon', 'SEO', 'Teknik'],
      seoTitle: 'Web Sitesi Hızlandırma Teknikleri 2024 | Site Performans Optimizasyonu',
      seoDescription: 'Web sitenizin yükleme hızını artırmak için kapsamlı rehber. Görsel optimizasyonu, caching, CDN kullanımı ve performans ölçüm teknikleri.',
      seoKeywords: 'web sitesi hızlandırma, site performans optimizasyonu, sayfa hızı artırma, web performans, site hızı SEO'
    }
  ];

  useEffect(() => {
    const foundPost = blogPosts.find(post => 
      post.title.toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') === slug
    );

    if (foundPost) {
      setPost(foundPost);
      
      // Set related posts (exclude current post)
      const related = blogPosts
        .filter(p => p.id !== foundPost.id && p.category === foundPost.category)
        .slice(0, 3);
      setRelatedPosts(related);

      // Update SEO
      document.title = foundPost.seoTitle;
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', foundPost.seoDescription);
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', foundPost.seoKeywords);
      }

      // Add structured data
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": foundPost.title,
        "description": foundPost.excerpt,
        "image": foundPost.image,
        "author": {
          "@type": "Organization",
          "name": foundPost.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "Webcoll",
          "logo": {
            "@type": "ImageObject",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/favicon.ico`
          }
        },
        "datePublished": foundPost.date,
        "dateModified": foundPost.date,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/${slug}`
        }
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Blog yazısı bulunamadı</h1>
          <Link to="/blog" className="text-blue-600 hover:text-purple-600">
            Blog sayfasına dön
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-bookmark-line mr-2"></i>
              {post.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center justify-center text-gray-600 space-x-6">
              <div className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                <span>{post.author}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                <span>{post.date}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-time-line mr-2"></i>
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div 
              className="text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
          
          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Etiketler</h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
          
          {/* Social Share */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Paylaş</h3>
            <div className="flex space-x-4">
              <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                <i className="ri-facebook-fill"></i>
                <span>Facebook</span>
              </button>
              <button className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors">
                <i className="ri-twitter-fill"></i>
                <span>Twitter</span>
              </button>
              <button className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors">
                <i className="ri-linkedin-fill"></i>
                <span>LinkedIn</span>
              </button>
              <button className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">
                <i className="ri-whatsapp-fill"></i>
                <span>WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                İlgili <span className="text-blue-600">Yazılar</span>
              </h2>
              <p className="text-xl text-gray-600">
                Bu konuyla ilgili diğer yazılarımızı da inceleyebilirsiniz.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <article 
                  key={relatedPost.id}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden group"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-48 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {relatedPost.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <i className="ri-calendar-line mr-2"></i>
                      <span>{relatedPost.date}</span>
                      <span className="mx-2">•</span>
                      <i className="ri-time-line mr-1"></i>
                      <span>{relatedPost.readTime}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                      {relatedPost.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {relatedPost.excerpt}
                    </p>
                    
                    <Link 
                      to={`/blog/${relatedPost.title.toLowerCase()
                        .replace(/ğ/g, 'g')
                        .replace(/ü/g, 'u')
                        .replace(/ş/g, 's')
                        .replace(/ı/g, 'i')
                        .replace(/ö/g, 'o')
                        .replace(/ç/g, 'c')
                        .replace(/[^a-z0-9]/g, '-')
                        .replace(/-+/g, '-')
                        .replace(/^-|-$/g, '')}`}
                      className="inline-flex items-center text-blue-600 font-semibold hover:text-purple-600 transition-colors duration-300"
                    >
                      Devamını Oku
                      <i className="ri-arrow-right-line ml-2"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/905451485952"
          target="_blank"
          rel="noopener noreferrer"
          className="w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 transform hover:scale-110 animate-pulse"
        >
          <i className="ri-whatsapp-line text-white text-2xl"></i>
        </a>
      </div>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;